/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Helmet } from 'react-helmet';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import {
  ChevronNextIcon,
  ChevronPreviousIcon,
  DownloadIcon,
  FullscreenIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '@coursera/cds-icons';

import type { PdfDocument, PdfPage } from 'bundles/ent-website/globals';

import _t from 'i18n!nls/ent-website';

// Pdf viewer options for future use
export type PdfViewerOptions = {};

type Props = {
  url: string;
};

// PDF scale. Not the actdual dimension of the visible PDF.
// The PDF will be rendered at PDF_SCALE then scaled to fit the viewport.
// So a greater PDF_SCALE will result in a higher quality PDF.
const PDF_SCALE = 3.0;

// How many percents to zoom in/out per click
const PDF_ZOOM_STEP = 25;

// Original size of the canvas.
const PDF_CANVAS_ORIGINAL_PERCENT_SIZE = 90;

function PDFJSRenderer({ url }: Props) {
  const styles = {
    root: css`
      background: var(--cds-color-grey-200);
      border: 1px solid var(--cds-color-grey-300);
      display: flex;
      flex-direction: column;
      align-items: center;
      overflow: auto;
    `,

    toolbar: css`
      position: relative;
      width: 100%;
      height: var(--cds-spacing-600);
      background: var(--cds-color-grey-25);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 1px 3px var(--cds-color-grey-300);

      @media all and (display-mode: fullscreen) {
        position: fixed;
      }

      @media only screen and (max-width: 400px) {
        justify-content: flex-start;
      }
    `,

    downloadButton: css`
      position: absolute;
      right: 0;
      margin-right: var(--cds-spacing-300);
      ${breakpoints.down('xs')} {
        display: none;
      }
    `,

    downloadButtonMobile: css`
      position: absolute;
      right: 0;
      margin-right: var(--cds-spacing-300);
      ${breakpoints.up('sm')} {
        display: none;
      }

      @media only screen and (max-width: 400px) {
        margin-right: var(--cds-spacing-100);
      }
    `,

    canvas: css`
      width: ${PDF_CANVAS_ORIGINAL_PERCENT_SIZE}%;
      margin-top: var(--cds-spacing-400);
      margin-bottom: var(--cds-spacing-400);
      box-shadow: 1px 1px 3px var(--cds-color-grey-300);
    `,
  };

  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const pdfViewerRef = React.useRef<HTMLDivElement>(null);
  const [reload, setReload] = React.useState(false);
  const [pdf, setPdf] = React.useState<PdfDocument | null>(null);
  const [pageNumber, setPageNumber] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [zoom, setZoom] = React.useState(100);

  React.useEffect(() => {
    // Do not load on SSR
    if (typeof window === 'undefined') {
      return;
    }

    const pdfjs = window.pdfjsLib;

    if (typeof pdfjs === 'undefined') {
      // recheck after 100ms
      window.setTimeout(() => setReload(!reload), 100);
      return;
    }

    pdfjs.getDocument(url).promise.then((doc: PdfDocument) => {
      setPdf(doc);
      setPageNumber(1);
      setTotalPages(doc.numPages);
    });
  }, [url, reload]);

  React.useEffect(() => {
    if (!pdf) {
      return;
    }
    if (pageNumber === 0) {
      return;
    }

    // Fetch the first page
    pdf.getPage(pageNumber).then((page: PdfPage) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const viewport = page.getViewport({ scale: PDF_SCALE });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const context = canvas?.getContext('2d');
      if (!context) {
        console.error('failed to get canvas context');
        return;
      }

      const renderContext = {
        canvasContext: context,
        viewport,
      };

      page.render(renderContext);
    });
  }, [pdf, pageNumber]);

  const handlePrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handleZoomIn = () => {
    setZoom(Math.min(200, zoom + PDF_ZOOM_STEP));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(25, zoom - PDF_ZOOM_STEP));
  };

  const handleFullscreen = () => {
    if (pdfViewerRef.current) {
      pdfViewerRef.current.requestFullscreen();
    }
  };
  const handleDownload = () => {
    window.open(url);
  };

  const zoomedCanvasWidth = PDF_CANVAS_ORIGINAL_PERCENT_SIZE * (zoom / 100);

  return (
    <>
      <Helmet>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.min.js" />
      </Helmet>
      <div css={styles.root} ref={pdfViewerRef} data-testid="pdfjs_renderer">
        <div css={styles.toolbar}>
          <Button variant="ghost" size="small" onClick={handlePrevPage}>
            <ChevronPreviousIcon color="default" />
          </Button>
          <Typography2 component="p">
            {pageNumber} / {totalPages}
          </Typography2>
          <Button variant="ghost" size="small" onClick={handleNextPage}>
            <ChevronNextIcon color="default" />
          </Button>
          <Button variant="ghost" size="small" onClick={handleZoomOut}>
            <ZoomOutIcon color="default" />
          </Button>
          <Typography2 component="p">{zoom}%</Typography2>
          <Button variant="ghost" size="small" onClick={handleZoomIn}>
            <ZoomInIcon color="default" />
          </Button>
          <Button variant="ghost" size="small" onClick={handleFullscreen}>
            <FullscreenIcon color="default" />
          </Button>
          <Button css={styles.downloadButtonMobile} variant="ghost" size="small" onClick={handleDownload}>
            <DownloadIcon color="default" />
          </Button>
          <Button
            css={styles.downloadButton}
            variant="ghost"
            size="small"
            onClick={handleDownload}
            icon={<DownloadIcon />}
            iconPosition="after"
          >
            {_t('Download')}
          </Button>
        </div>
        <canvas ref={canvasRef} css={[styles.canvas, { width: `${zoomedCanvasWidth}%` }]} />
      </div>
    </>
  );
}
export default PDFJSRenderer;
